import React, { useState, useEffect, useCallback } from "react";
import style from "./style.module.css";
import { useCookies } from "react-cookie";
import { authenticatedFetch } from "../../utils/api";
import { API_BASE } from "../../config/api";
import { parseApiError } from "../../utils/parseApiError";
import useSnackbar from "../../context/Snackbar/useSnackbar";
import { usePermissions } from "../../hooks/usePermissions";

interface Store {
  store_id: number;
  store_name: string;
  channel_type?: string;
}

interface DeliverySlot {
  delivery_slot_id: number;
  store_id: number;
  slot_name?: string | null;
  start_time: string;
  end_time: string;
  max_orders_per_slot: number;
  is_active: boolean;
}

interface EditSlot {
  delivery_slot_id?: number;
  slot_name: string;
  start_time: string;
  end_time: string;
  max_orders_per_slot: string;
  is_active: boolean;
}

const EMPTY_SLOT: EditSlot = { slot_name: "", start_time: "08:00", end_time: "12:00", max_orders_per_slot: "20", is_active: true };

const DeliverySlotsPage = () => {
  const [cookies] = useCookies();
  const snack = useSnackbar();
  const permissions = usePermissions();

  const [stores, setStores] = useState<Store[]>([]);
  // True once the store fetch has resolved with at least one owned store,
  // but none of them support delivery — either pos_only (no mobile
  // presence at all, docs/store-channel-access-control-plan.md, Step 3) or
  // pickup_only (mobile, but createSlot's supportsOrderType check rejects
  // an order_type: 'delivery' slot for it — docs/pickup-slots-plan.md).
  // Filtering to exactly 'pickup_and_delivery' here, not just "not
  // pos_only", avoids a dead-end: letting a pickup_only store be selected
  // only to have every "Add Slot" attempt 400. Distinct from "owns zero
  // stores at all" (a pre-existing, unrelated empty-list case already
  // handled by the "Select a store…" message below).
  const [noEligibleStores, setNoEligibleStores] = useState(false);
  const [storeId, setStoreId] = useState<number | null>(null);
  const [slots, setSlots] = useState<DeliverySlot[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editSlot, setEditSlot] = useState<EditSlot>(EMPTY_SLOT);
  const [isSaving, setIsSaving] = useState(false);

  // Auto-selects when there's only one store (the common single-store Admin
  // case), otherwise a dropdown lets an Owner (or an Admin owning several
  // stores) pick which store's slots to manage — same pattern as CashDrawerPage.
  useEffect(() => {
    authenticatedFetch(`${API_BASE}/store`, { method: "GET" }, cookies.auth)
      .then((res) => (res.ok ? res.json() : { data: [] }))
      .then((data) => {
        const list: Store[] = data.data || data || [];
        const eligible = list.filter((s) => s.channel_type === 'pickup_and_delivery');
        setStores(eligible);
        setNoEligibleStores(list.length > 0 && eligible.length === 0);
        if (eligible.length === 1) setStoreId(eligible[0].store_id);
      })
      .catch(() => setStores([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSlots = useCallback(async () => {
    if (!storeId) return;
    setIsLoading(true);
    try {
      const response = await authenticatedFetch(`${API_BASE}/deliveryslot?store_id=${storeId}&order_type=delivery`, { method: "GET" }, cookies.auth);
      if (response.ok) {
        const data = await response.json();
        setSlots(Array.isArray(data?.data) ? data.data : []);
      } else {
        snack.onResponse({ message: await parseApiError(response, "Failed to load delivery slots"), status: response.status });
      }
    } catch (error) {
      snack.onResponse({ message: "Failed to load delivery slots", status: 500 });
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId, cookies.auth]);

  useEffect(() => {
    fetchSlots();
  }, [fetchSlots]);

  const handleAdd = () => {
    setEditSlot(EMPTY_SLOT);
    setShowAddModal(true);
  };

  const handleEdit = (slot: DeliverySlot) => {
    setEditSlot({
      delivery_slot_id: slot.delivery_slot_id,
      slot_name: slot.slot_name || "",
      start_time: slot.start_time.slice(0, 5),
      end_time: slot.end_time.slice(0, 5),
      max_orders_per_slot: String(slot.max_orders_per_slot),
      is_active: slot.is_active,
    });
    setShowEditModal(true);
  };

  const handleToggleActive = async (slot: DeliverySlot) => {
    try {
      const response = await authenticatedFetch(`${API_BASE}/deliveryslot/${slot.delivery_slot_id}`, {
        method: "PUT",
        body: JSON.stringify({ is_active: !slot.is_active }),
      }, cookies.auth);
      if (response.ok) {
        snack.onResponse({ message: `Slot marked ${!slot.is_active ? "Active" : "Inactive"}`, status: 200 });
        await fetchSlots();
      } else {
        snack.onResponse({ message: await parseApiError(response, "Failed to update slot status"), status: response.status });
      }
    } catch (error) {
      snack.onResponse({ message: "Network error updating slot status", status: 500 });
    }
  };

  const handleDelete = async (slot: DeliverySlot) => {
    const label = `${slot.slot_name ? slot.slot_name + ' ' : ''}${slot.start_time.slice(0, 5)}–${slot.end_time.slice(0, 5)}`;
    if (!window.confirm(`Delete the ${label} slot?`)) return;
    try {
      const response = await authenticatedFetch(`${API_BASE}/deliveryslot/${slot.delivery_slot_id}`, { method: "DELETE" }, cookies.auth);
      if (response.ok) {
        snack.onResponse({ message: "Delivery slot deleted", status: 200 });
        await fetchSlots();
      } else {
        snack.onResponse({ message: await parseApiError(response, "Failed to delete delivery slot"), status: response.status });
      }
    } catch (error) {
      snack.onResponse({ message: "Network error deleting delivery slot", status: 500 });
    }
  };

  const handleSave = async (isEdit: boolean) => {
    if (!storeId) return;
    if (!editSlot.start_time || !editSlot.end_time) {
      snack.onResponse({ message: "Start and end time are required", status: 400 });
      return;
    }
    if (editSlot.start_time >= editSlot.end_time) {
      snack.onResponse({ message: "Start time must be before end time", status: 400 });
      return;
    }
    const maxOrders = parseInt(editSlot.max_orders_per_slot, 10);
    if (isNaN(maxOrders) || maxOrders <= 0) {
      snack.onResponse({ message: "Max orders per slot must be a positive number", status: 400 });
      return;
    }

    setIsSaving(true);
    try {
      const payload: Record<string, unknown> = {
        slot_name: editSlot.slot_name.trim() || null,
        start_time: editSlot.start_time,
        end_time: editSlot.end_time,
        max_orders_per_slot: maxOrders,
        is_active: editSlot.is_active,
      };
      if (!isEdit) {
        payload.store_id = storeId;
        payload.order_type = 'delivery';
      }

      const url = isEdit ? `${API_BASE}/deliveryslot/${editSlot.delivery_slot_id}` : `${API_BASE}/deliveryslot`;
      const response = await authenticatedFetch(url, {
        method: isEdit ? "PUT" : "POST",
        body: JSON.stringify(payload),
      }, cookies.auth);

      if (!response.ok) {
        snack.onResponse({ message: await parseApiError(response, "Failed to save delivery slot"), status: response.status });
        return;
      }

      setShowAddModal(false);
      setShowEditModal(false);
      await fetchSlots();
      snack.onResponse({ message: isEdit ? "Delivery slot updated" : "Delivery slot created", status: 200 });
    } catch (error) {
      snack.onResponse({ message: "Network error saving delivery slot", status: 500 });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className={style.wrapper}>
      <div className={style.header}>
        <h1 className={style.title}>Delivery Slots</h1>
        {stores.length > 1 && (
          <select
            className={style.storeSelect}
            value={storeId ?? ""}
            onChange={(e) => setStoreId(e.target.value ? Number(e.target.value) : null)}
          >
            <option value="">Select a store…</option>
            {stores.map((s) => (
              <option key={s.store_id} value={s.store_id}>{s.store_name}</option>
            ))}
          </select>
        )}
        {permissions.canAdd && (
          <button className={style.addBtn} onClick={handleAdd} disabled={!storeId}>
            + Add Slot
          </button>
        )}
      </div>

      {noEligibleStores ? (
        <div className={style.loadingState}>None of your stores support delivery — delivery slots aren't available.</div>
      ) : !storeId ? (
        <div className={style.loadingState}>Select a store to manage its delivery slots.</div>
      ) : isLoading ? (
        <div className={style.loadingState}>Loading…</div>
      ) : (
        <div className={style.tableContainer}>
          <table className={style.dataTable}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Start Time</th>
                <th>End Time</th>
                <th>Max Orders / Slot</th>
                <th>Status</th>
                {permissions.canAdd && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {slots.length === 0 ? (
                <tr>
                  <td colSpan={6} className={style.emptyText}>No delivery slots yet for this store.</td>
                </tr>
              ) : (
                slots.map((slot) => (
                  <tr key={slot.delivery_slot_id}>
                    <td>{slot.slot_name || '—'}</td>
                    <td>{slot.start_time.slice(0, 5)}</td>
                    <td>{slot.end_time.slice(0, 5)}</td>
                    <td>{slot.max_orders_per_slot}</td>
                    <td>
                      <span
                        className={`${style.statusBadge} ${slot.is_active ? style.statusActive : style.statusInactive} ${permissions.canAdd ? style.statusBadgeClickable : ''}`}
                        onClick={permissions.canAdd ? () => handleToggleActive(slot) : undefined}
                        title={permissions.canAdd ? "Click to toggle" : undefined}
                      >
                        {slot.is_active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    {permissions.canAdd && (
                      <td>
                        <button className={style.smallBtnGhost} onClick={() => handleEdit(slot)}>Edit</button>
                        <button className={style.smallBtnDanger} onClick={() => handleDelete(slot)}>Delete</button>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {(showAddModal || showEditModal) && (
        <div className={style.modalOverlay}>
          <div className={style.modal}>
            <h2>{showAddModal ? "Add Delivery Slot" : "Update Delivery Slot"}</h2>
            <div className={style.modalContent}>
              <label className={style.modalLabel}>
                Slot Name (optional)
                <input
                  type="text"
                  value={editSlot.slot_name}
                  onChange={(e) => setEditSlot({ ...editSlot, slot_name: e.target.value })}
                  className={style.modalInput}
                  placeholder="e.g. Morning, Evening"
                  maxLength={50}
                />
              </label>
              <label className={style.modalLabel}>
                Start Time
                <input
                  type="time"
                  value={editSlot.start_time}
                  onChange={(e) => setEditSlot({ ...editSlot, start_time: e.target.value })}
                  className={style.modalInput}
                />
              </label>
              <label className={style.modalLabel}>
                End Time
                <input
                  type="time"
                  value={editSlot.end_time}
                  onChange={(e) => setEditSlot({ ...editSlot, end_time: e.target.value })}
                  className={style.modalInput}
                />
              </label>
              <label className={style.modalLabel}>
                Max Orders / Slot
                <input
                  type="number"
                  min={1}
                  value={editSlot.max_orders_per_slot}
                  onChange={(e) => setEditSlot({ ...editSlot, max_orders_per_slot: e.target.value })}
                  className={style.modalInput}
                />
              </label>
              <label className={style.modalCheckboxLabel}>
                <input
                  type="checkbox"
                  checked={editSlot.is_active}
                  onChange={(e) => setEditSlot({ ...editSlot, is_active: e.target.checked })}
                />
                Active
              </label>

              <div className={style.modalButtons}>
                <button
                  className={style.saveBtn}
                  onClick={() => handleSave(showEditModal)}
                  disabled={isSaving}
                >
                  {isSaving ? "Saving…" : (showAddModal ? "Add Slot" : "Update Slot")}
                </button>
                <button
                  className={style.cancelBtn}
                  onClick={() => { setShowAddModal(false); setShowEditModal(false); }}
                  disabled={isSaving}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeliverySlotsPage;
